import React from 'react';
import { Button, Flex, Select, Typography } from 'shared/_ui';
import styles from './styles.module.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationScheme } from '../lib/validationScheme';
import { ITariff, Payment, useCreatePaymentRefresh } from 'shared/api';
import { useForm, Controller } from 'react-hook-form';
import { createPaymentFrame, createPaymentWidgetScript } from 'entities/Tariff';
import { useTranslation } from 'react-i18next';
import { getProviderName } from '../lib/getProviderName';
import { PAYMENT_PROVIDER_IDS } from '../const';
import { PaymentModal } from 'entities/Payment';
import { useModal } from 'shared/lib';
import { sendAnalyticsEvent } from 'shared/lib/analytics';
import { getCheckoutUrl, requiresExternalCheckout } from '../lib/checkoutNavigation';
import { ExternalCheckoutLink } from './ExternalCheckoutLink';

type PaymentFormProps = {
  tariffId: number;
  tariff: ITariff;
};

const getAnalyticsPlan = (tariff: ITariff) => {
  const periodName = tariff.periodName.toLowerCase();

  if (periodName === 'year') {
    return 'yearly';
  }

  if (periodName === 'month' && tariff.periodValue === 1) {
    return 'monthly';
  }

  return `${tariff.periodValue}_${periodName}`;
};

const PaymentForm: React.FC<PaymentFormProps> = ({ tariffId, tariff }) => {
  const { t } = useTranslation();

  const [currentPayment, setCurrentPayment] = React.useState<Payment | null>(
    null
  );

  const { isOpen, isMount, openModal, setIsOpen } = useModal(false);

  const [createPayment] = useCreatePaymentRefresh();
  const [isCreatingPayment, setIsCreatingPayment] = React.useState(false);
  const [readyCheckout, setReadyCheckout] = React.useState<string | null>(null);
  const creationInFlight = React.useRef(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ providerId: number }>({
    mode: 'onSubmit',
    resolver: yupResolver(validationScheme),
  });

  const onSubmit = async (data: { providerId: number }) => {
    if (creationInFlight.current || readyCheckout) return;
    creationInFlight.current = true;
    setIsCreatingPayment(true);
    let checkoutPrepared = false;
    try {
      const response = await createPayment({ ...data, tariffId }).unwrap();
      sendAnalyticsEvent('purchase', {
        value: response.amount,
        currency: response.currency,
        plan: getAnalyticsPlan(tariff),
      });

      if (data.providerId === PAYMENT_PROVIDER_IDS["STRIPE"] || response.widgetType === "PLATEGA") {
        const checkoutUrl = getCheckoutUrl(response.paymentUrl);
        if (requiresExternalCheckout()) {
          setReadyCheckout(checkoutUrl);
          checkoutPrepared = true;
        } else {
          window.location.assign(checkoutUrl);
        }
        return;
      }

      setCurrentPayment(response);
      openModal();
      createPaymentWidgetScript(response?.widgetUrl);
      if (response?.paymentUrl) {
        setTimeout(() => {
          createPaymentFrame(response.paymentUrl);
        }, 100);
      }
    } catch (error) {
      console.error(t('shared.error.base'));
    } finally {
      if (!checkoutPrepared) creationInFlight.current = false;
      setIsCreatingPayment(false);
    }
  };
  function sortFunction(a : number, b : number) : (0 | -1 | 1) {
    const order = [8, 4];
    return order.indexOf(a) > order.indexOf(b) ? 1 : -1;
    
  }
  const options = Object.values(PAYMENT_PROVIDER_IDS).sort(sortFunction).map<{
    label: string;
    value: number;
  }>((id) => ({
    label: t(getProviderName(id)),
    value: id,
  }));

  const [inPayment, setInPayment] = React.useState(false)
  React.useEffect(() => {
    if (!inPayment && !isOpen) {
      setCurrentPayment(null);
      setInPayment(false);
      return;
    }
    if (inPayment && !isOpen) {
      window.location.reload();
      return
    }
    setInPayment(true);
  }, [isOpen]);

  if (readyCheckout) return <ExternalCheckoutLink url={readyCheckout} />;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" spacing={28} className={styles.form}>
          <Flex direction="column" spacing={16}>
            <Typography.Title level={1}>{t('payment.title')}</Typography.Title>
            <Flex direction="column" spacing={8}>
              <Typography.Text size={1} type="secondary">
                {t('payment.paymentMethod')}
              </Typography.Text>
              <Controller
                control={control}
                name="providerId"
                render={({ field: { value, onChange } }) => (
                  <Select
                    placeholder={t('payment.selectPaymentMethod')}
                    options={options}
                    value={options?.find((c) => c.value === value)}
                    onChange={(value: any) => onChange(value.value)}
                    isDisabled={isCreatingPayment}
                  />
                )}
              />
              {errors.providerId && (
                <Typography.Text type="danger">
                  {t(
                    errors.providerId?.message
                      ? errors.providerId?.message
                      : 'shared.error.base'
                  )}
                </Typography.Text>
              )}
            </Flex>
          </Flex>
          <Button type="primary" loading={isCreatingPayment}>
            <Typography.Text size={2} strong>
              {t('payment.pay')}
            </Typography.Text>
          </Button>
        </Flex>
      </form>
      {isMount && (
        <PaymentModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          currentPayment={currentPayment}
        />
      )}
    </>
  );
};

export { PaymentForm };
